import { format } from "date-fns";
import { Eye, Plus, Send } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { EmptyState, ErrorState, PageLoading } from "../../components/ui/loading";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  useAudiences,
  useCampaigns,
  useCreateCampaign,
  usePreviewCampaign,
  useSendCampaign,
} from "../../hooks/use-comms";

const mergeFields = [
  "{insured_name}",
  "{address}",
  "{building_name}",
  "{certificate_period_from}",
  "{certificate_period_to}",
  "{valuer_name}",
  "{reinstatement_cost_estimate}",
];

function textToHtml(text: string): string {
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br />")}</p>`)
    .join("\n");
}

function CampaignForm({
  onSave,
  onCancel,
  isSaving,
}: {
  onSave: (input: { subject: string; bodyText: string; audienceId: string | null }) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const { data: audienceData } = useAudiences();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audienceId, setAudienceId] = useState("");
  const audiences = audienceData?.items ?? [];

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="t-subject" className="text-xs font-medium text-muted-foreground block mb-1">
          Subject
        </label>
        <input
          id="t-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="template-subject"
        />
      </div>
      <div>
        <label htmlFor="t-body" className="text-xs font-medium text-muted-foreground block mb-1">
          Body
        </label>
        <textarea
          id="t-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="template-body"
        />
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">Available Merge Fields</p>
        <div className="flex flex-wrap gap-1">
          {mergeFields.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setBody((b) => `${b}${f}`)}
              className="rounded bg-muted px-2 py-0.5 text-xs font-mono hover:bg-accent transition-colors"
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label htmlFor="t-audience" className="text-xs font-medium text-muted-foreground block mb-1">
          Target Audience
        </label>
        <select
          id="t-audience"
          value={audienceId}
          onChange={(e) => setAudienceId(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"
          data-testid="template-audience"
        >
          <option value="">Select audience...</option>
          {audiences.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-2 pt-2">
        <Button
          onClick={() => onSave({ subject, bodyText: body, audienceId: audienceId || null })}
          disabled={!subject.trim() || !body.trim() || isSaving}
          data-testid="template-save"
        >
          {isSaving ? "Saving..." : "Save Draft"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function TemplatesPage(): React.ReactElement {
  const { data, isLoading, error, refetch } = useCampaigns();
  const createCampaign = useCreateCampaign();
  const previewCampaign = usePreviewCampaign();
  const sendCampaign = useSendCampaign();
  const [showForm, setShowForm] = useState(false);
  const [previewCount, setPreviewCount] = useState<{ campaignId: string; count: number } | null>(null);

  if (isLoading) return <PageLoading />;
  if (error) return <ErrorState message="Failed to load campaigns." onRetry={() => refetch()} />;

  const campaigns = data?.items ?? [];

  function handleSave(input: { subject: string; bodyText: string; audienceId: string | null }): void {
    createCampaign.mutate(
      {
        subject: input.subject,
        bodyText: input.bodyText,
        bodyHtml: textToHtml(input.bodyText),
        audienceId: input.audienceId,
      },
      { onSuccess: () => setShowForm(false) },
    );
  }

  async function handlePreview(campaignId: string): Promise<void> {
    const result = await previewCampaign.mutateAsync(campaignId);
    setPreviewCount({ campaignId, count: result.count });
  }

  async function handleSend(campaignId: string): Promise<void> {
    const result = await previewCampaign.mutateAsync(campaignId);
    const confirmed = window.confirm(`Send this campaign to ${result.count} recipient(s)?`);
    if (!confirmed) return;
    await sendCampaign.mutateAsync(campaignId);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{campaigns.length} campaigns</p>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="create-template">
          <Plus className="h-3.5 w-3.5" /> Create New
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Subject</TableHead>
            <TableHead>Audience</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead className="w-28">Created</TableHead>
            <TableHead className="w-28">Sent</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <EmptyState title="No campaigns" description="No email campaigns yet." />
              </TableCell>
            </TableRow>
          ) : (
            campaigns.map((c) => (
              <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                <TableCell className="font-medium">{c.subject}</TableCell>
                <TableCell className="text-muted-foreground">{c.audience?.name ?? "None"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.status}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(c.createdAt), "dd MMM yyyy")}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {c.sentAt ? format(new Date(c.sentAt), "dd MMM yyyy HH:mm") : "Never"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePreview(c.id)}
                      data-testid={`campaign-preview-${c.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={c.status !== "DRAFT" || sendCampaign.isPending}
                      onClick={() => handleSend(c.id)}
                      data-testid={`campaign-send-${c.id}`}
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {previewCount && (
        <Dialog open onOpenChange={() => setPreviewCount(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recipient Preview</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground" data-testid="campaign-preview-count">
              This campaign will be sent to <strong>{previewCount.count}</strong> recipient(s).
            </p>
            <Button variant="ghost" onClick={() => setPreviewCount(null)}>
              Close
            </Button>
          </DialogContent>
        </Dialog>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compose Email Campaign</DialogTitle>
          </DialogHeader>
          <CampaignForm onSave={handleSave} onCancel={() => setShowForm(false)} isSaving={createCampaign.isPending} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
